import { resolveText } from '../lib/i18n/customer'
import { SAME } from '../lib/i18n/same'

// The layout document, as the server defines it.
//
// A mirror of internal/layout. Kept by hand rather than generated, because it is
// small and stable and a generator is a build step to maintain — but it is a mirror:
// the server validates, and nothing here is the authority on what is allowed.

export type LayoutKind = 'display' | 'kiosk' | 'ticket' | 'counter'

export type KioskScreen = 'attract' | 'choosing' | 'issued'

/** A counter display's two screens: a ticket at the desk, or none. */
export type CounterScreen = 'called' | 'free'

/** A main display's board, and its Called screen. In the browser the Called screen's
 *  elements carry screens ['call']; on the wire they live in document.called (calledScreen.ts). */
export type DisplayScreen = 'board' | 'call'

/** Any design's screen; only kiosk, counter and main display designs have more than one. */
export type Screen = KioskScreen | CounterScreen | DisplayScreen

/** How a counter display's element enters; absent is slideDown. 'draw' is a rule's only. */
/** How a counter display changes screen; absent is the flip clock. */
export type ScreenChange = 'flip' | 'slide' | 'fade' | 'none'
export type ElementMotion = 'slideDown' | 'rise' | 'flip' | 'fade' | 'none' | 'draw'

export type ElementKind =
  | 'calls'
  | 'queues'
  | 'waitingTotal'
  | 'clock'
  | 'branchName'
  | 'languages'
  | 'services'
  | 'ticketNumber'
  | 'ticketFacts'
  | 'ticketPosition'
  | 'ticketWait'
  | 'qr'
  | 'dateTime'
  | 'validUntil'
  | 'text'
  | 'image'
  | 'rule'
  | 'media'
  | 'button'
  | 'ticker'
  | 'deskName'
  | 'serviceName'
  | 'deskStatus'
  | 'deskNumber'
  | 'statusPill'
  | 'clerkName'
  | 'logo'
  | 'panel'

/** A string in the languages a branch serves. */
export type LayoutText = Record<string, string>

export interface ElementStyle {
  scale?: number
  weight?: number
  align?: 'left' | 'center' | 'right'
  colour?: string
  background?: string
  /** The colour a freshly called number is marked in. */
  accent?: string
  radius?: number
  uppercase?: boolean
  /** A button lifted off its background. */
  shadow?: boolean
  /** The colour of a line round a button or panel: a token name or #rrggbb. */
  border?: string
  /** That line's width, 1 to 16; absent is 2. */
  borderWidth?: number
  /** A rule painted in the brand's accent gradient instead of its colour. */
  gradient?: boolean
}

export interface ElementOptions {
  columns?: number
  rows?: number
  /** Drop the first few calls before filling the grid, so a board can show one
   *  number large and the ones before it small without repeating itself. */
  skip?: number
  /** How a call that has just arrived is marked. A fill behind a number a metre
   *  tall is a lit panel; a fill behind one of six is a highlighted cell. */
  highlight?: 'fill' | 'bar' | 'underline' | 'none'
  showDesk?: boolean
  showPrefix?: boolean
  highlightSeconds?: number
  format?: string
  /** The website a QR code opens when its format is 'website' (internal/layout,
   *  QRWebsite). A QR code's only; http or https, at most 100 characters. */
  url?: string
  /** How many rows of a printed ticket this element's text may take.
   *
   *  Absent means no limit, which is what every design drawn before this existed
   *  means and must go on meaning. A printed ticket is the only place it is
   *  allowed: paper is 576 dots across and nothing on it can be scrolled. What it
   *  does to the text is in fit.ts. */
  maxRows?: 1 | 2 | 3
  /** How far a text with no row limit shrinks to fit, as a share of its size; absent is 0.6 (fit.ts). */
  minScale?: number
  /** Which services a kiosk's grid shows, in the order it shows them.
   *
   *  Absent or empty means every service the branch offers, in the catalogue's
   *  order, which is what every grid did before this existed. One the branch does
   *  not offer is left out there; kioskMenu.ts says why. */
  serviceIds?: string[]
  /** Retired with icons (31 Oct 2026): kept on stored designs, read by nothing. */
  iconPosition?: 'left' | 'top'
  /** A line's thickness in canvas units (dots on a ticket), 1 to 16; absent derives it from the box (rule.ts). */
  thickness?: number
  /** What a queues element shows: counts (absent) or a grid of the waiting numbers (waitingGrid.ts). */
  view?: 'counts' | 'grid'
}

/** What a playlist item is. The server checks it against the asset's own type. */
export type MediaKind = 'image' | 'video'

/** How an item fills its area: Orchestra's positions without stretch. Absent is fit. */
export type MediaFit = 'fill' | 'fit' | 'centre'

export interface MediaItem {
  assetId: string
  kind: MediaKind
  /** How long it stays. Absent or 0: a picture for ten seconds, a film to its end. */
  seconds?: number
  /** fill, fit or centre; absent is fit. */
  fit?: MediaFit
  /** A film plays with its sound; absent is muted. Films only. */
  sound?: boolean
}

/** A time at which a playlist plays a different list: ISO days (Monday is 1),
 *  "HH:MM" to "HH:MM" within one day, in the branch's own clock. */
export interface MediaSlot {
  days: number[]
  from: string
  to: string
  items: MediaItem[]
}

export interface LayoutMedia {
  items: MediaItem[]
  /** Absent on what a board is sent: the server resolves the times and sends the
   *  list playing now (internal/layout/playing.go). */
  schedule?: MediaSlot[]
  /** 'playlist' plays a saved playlist, or with no playlistId campaigns only; absent or 'own' plays items. */
  source?: 'own' | 'playlist'
  playlistId?: string
  /** The playing playlist's ticker lines, on a screen's answer only. */
  ticker?: LayoutText[]
}

/** What pressing a button does (internal/layout/pages.go). */
export type ButtonAction =
  | { kind: 'service'; serviceId: string }
  | { kind: 'page'; pageId: string }
  | { kind: 'back' }
  | { kind: 'booking' }

/** One page of a kiosk's menu. A customer never reads the name: a button opens
 *  the page, and the button has its own words. */
export interface LayoutPage {
  id: string
  name?: LayoutText
}

/** The page a design with no pages has. */
export const MENU_PAGE = 'menu'

export interface LayoutElement {
  id: string
  kind: ElementKind
  x: number
  y: number
  w: number
  h: number
  z: number
  /** Kept in the design, drawn nowhere. */
  hidden?: boolean
  /** A counter display's only (layout/counterMotion.ts). */
  motion?: ElementMotion
  /** This element's own font (layout/fonts.ts); absent takes the design's. */
  font?: string
  screens?: Screen[]
  /** Which menu pages this is on, when it is on the choosing screen. Empty means
   *  every page — a header, a logo, and everything saved before pages existed. */
  pages?: string[]
  /** A button's: what pressing it does and its second line. Its assetId is a picture filling it. */
  action?: ButtonAction
  subtitle?: LayoutText
  /** Retired (31 Oct 2026): kept on stored designs, drawn nowhere. */
  icon?: string
  content?: LayoutText
  /** A realistic value to draw in the editor in place of the one a real visit
   *  would supply, so that a designer sees a long name overflow before a customer
   *  does. The editor's preview honours it and nothing else does — the printer has
   *  no code path that reads it. */
  previewText?: string
  assetId?: string
  /** What a playlist plays, and when. Only on a 'media' element. */
  media?: LayoutMedia
  style?: ElementStyle
  options?: ElementOptions
}

export type CanvasTheme = 'dark' | 'light' | 'studio'

/** What paints the ground; absent is colour. */
export type BackgroundFill = 'colour' | 'gradient' | 'image'

export interface LayoutCanvas {
  width: number
  height: number
  theme?: CanvasTheme
  background?: string
  backgroundFill?: BackgroundFill
  /** A preset's name (look.ts GRADIENTS). */
  backgroundGradient?: string
  /** Kept while another fill shows, so switching back is instant. */
  backgroundImage?: { assetId: string }
  /** The design's brand logo, which a logo element with no picture of its own draws; absent is none. */
  logo?: { source?: 'qmeter' | 'organisation'; assetId?: string }
  /** A slow drifting light behind a kiosk's welcome screen. */
  ambient?: boolean
  /** A kiosk that starts at its menu, with no welcome screen. */
  skipWelcome?: boolean
  /** A counter display's screen change; absent is the flip clock. */
  screenChange?: ScreenChange
  /** The font every text is set in (layout/fonts.ts); absent is the standard one. */
  font?: string
}

/** One thing wrong with a design, and the elements it is about (none for a missing kind). */
export interface Fault {
  text: string
  ids: string[]
}

export interface LayoutDocument {
  kind: LayoutKind
  canvas: LayoutCanvas
  elements: LayoutElement[]
  /** A kiosk's menu pages, the menu first. Absent means one page, MENU_PAGE. */
  pages?: LayoutPage[]
  /** A main display's screen for each call; absent shows the board only. Its elements
   *  are in `elements` with screens ['call'] while in the browser (calledScreen.ts). */
  called?: LayoutCalled
  /** The design's own built-in words: word id → language → text (layout/words.ts). */
  words?: Record<string, LayoutText>
  /** The design's customer languages in tab order; a screen starts in the first (layout/designLanguages.ts). */
  languages?: string[]
  /** A display's seconds per language, each in turn; absent shows the first only. */
  rotateSec?: number
}

export interface LayoutCalled {
  /** Seconds each call shows, 3 to 30; absent is 5. */
  showSec?: number
  /** On the wire only; unpacked into the document's elements in the browser. */
  elements?: LayoutElement[]
}

/** A design's pages in order, the menu first. */
export function pageIds(doc: Pick<LayoutDocument, 'pages'>): string[] {
  return doc.pages && doc.pages.length > 0 ? doc.pages.map((p) => p.id) : [MENU_PAGE]
}

/**
 * Resolve a multilingual string the way the server does (layout.Text.InFor): the
 * language asked for, the organisation's default, English, then anything at all.
 * A missing translation shows the customer something.
 */
export function textIn(text: LayoutText | undefined, lang: string, orgDefault?: string): string {
  return resolveText(text, lang, orgDefault)
}

/**
 * A button's own words in lang only, never another language's: '' when cleared there
 * (a blank kept under lang), undefined when never set (the caller shows its default).
 */
export function buttonWords(text: LayoutText | undefined, lang: string): string | undefined {
  const same = text?.[SAME]?.trim()
  if (same) return same
  if (!text || !Object.prototype.hasOwnProperty.call(text, lang)) return undefined
  return text[lang]!.trim()
}

/**
 * A colour from the document as a CSS value.
 *
 * Token names are the normal case and are what keeps a layout following the theme
 * it is shown in; a literal is passed through for the brand colour a customer will
 * insist on. Anything else resolves to the fallback rather than to an invalid
 * property that silently inherits something surprising.
 */
export function colour(value: string | undefined, fallback: string): string {
  if (!value) return fallback
  if (value.startsWith('--')) return `var(${value}, ${fallback})`
  if (/^#[0-9a-fA-F]{3}$|^#[0-9a-fA-F]{6}$/.test(value)) return value
  return fallback
}

/**
 * Substitute "{{key}}" in one string, the way internal/layout.Resolve does on the
 * server — this is that algorithm ported rather than reinvented, because a kiosk
 * and a hall board resolving spaced braces or a missing value differently from the
 * printer would be a design that reads one way on the wall and another on paper.
 *
 * One left-to-right scan rather than a series of String.replace calls, for the same
 * reason the Go version is one: a customer's own value must never be read a second
 * time for "{{...}}" in it, or a value of literal "{{wifi.psk}}" would expand into
 * whatever this map holds for that key.
 *
 * A key with no value becomes nothing, and one adjacent space is swallowed with it
 * — "Hello {{visitor.name}}" with no value gives "Hello", not "Hello ". Nothing
 * else about the text is touched: newlines survive, so a multi-line hall-board
 * notice stays multi-line.
 */
export function resolvePlaceholders(text: string, values: Record<string, string>): string {
  let out = ''
  let rest = text
  for (;;) {
    const i = rest.indexOf('{{')
    if (i < 0) {
      out += rest
      return out
    }
    const before = rest.slice(0, i)
    const afterOpen = rest.slice(i + 2)
    const j = afterOpen.indexOf('}}')
    if (j < 0) {
      // An unclosed brace is not a reference: everything from here on is literal
      // text, the same rule the tokeniser applies everywhere else.
      out += rest
      return out
    }
    const raw = afterOpen.slice(0, j)
    const key = raw.trim()
    rest = afterOpen.slice(j + 2)

    if (key === '') {
      // Not a key either way — "{{}}" is somebody's stray braces, written back
      // exactly as typed.
      out += before + '{{' + raw + '}}'
      continue
    }

    // hasOwnProperty rather than values[key] directly: a plain object's lookup
    // walks the prototype chain, so an unguarded read of a key like "toString"
    // would not come back undefined — it would come back the function itself,
    // which string concatenation below would stringify into the drawn text.
    // internal/layout.Resolve cannot have this problem at all, a Go map has no
    // prototype to walk, so a careless port is exactly where it would appear.
    // Unreachable today because save-time validation refuses a placeholder
    // naming any key the organisation has not declared — but this function is
    // exported, and the next caller may not come through a validated document.
    const value = Object.prototype.hasOwnProperty.call(values, key) ? values[key] : undefined
    if (value) {
      out += before + value
      continue
    }

    if (before.endsWith(' ')) {
      out += before.slice(0, -1)
      continue
    }
    out += before
    if (rest.startsWith(' ')) rest = rest.slice(1)
  }
}

/**
 * Every distinct key a piece of text refers to, in the order they first appear —
 * internal/layout.Placeholders, ported for the same reason resolvePlaceholders was.
 *
 * The editor needs it to answer two questions the server answers for itself: which
 * elements carry a value it cannot predict the length of, and which sample to stand
 * in for it while somebody is designing.
 *
 * "{{" is reserved for this and nothing else, and an unclosed brace is not a
 * reference — it is a customer's literal text, and guessing at what they meant is
 * how a notice ends up with half of itself missing.
 */
export function placeholderKeys(text: string): string[] {
  const out: string[] = []
  const seen = new Set<string>()
  let rest = text
  for (;;) {
    const i = rest.indexOf('{{')
    if (i < 0) return out
    rest = rest.slice(i + 2)
    const j = rest.indexOf('}}')
    if (j < 0) return out
    const key = rest.slice(0, j).trim()
    if (key !== '' && !seen.has(key)) {
      seen.add(key)
      out.push(key)
    }
    rest = rest.slice(j + 2)
  }
}

/**
 * What an element says while it is being designed: its own text, with the
 * designer's sample standing in for every placeholder in it.
 *
 * The same sample for each key, when an element names two. That is deliberately the
 * pessimistic reading — an element holding both a name and a reference number is
 * previewed as though both were as long as the longest thing typed — because the
 * question this feature exists to answer is whether the line can overflow, and an
 * optimistic preview of that is worth nothing.
 *
 * The surrounding words are kept rather than replaced wholesale: "Hello {{name}},
 * welcome" is nine characters of fixed text that take room on the paper too, and a
 * preview that dropped them would under-report by exactly those nine.
 */
export function withPreviewText(text: string, sample: string): string {
  const keys = placeholderKeys(text)
  if (keys.length === 0 || sample.trim() === '') return text
  const values: Record<string, string> = {}
  for (const key of keys) values[key] = sample
  return resolvePlaceholders(text, values)
}

/**
 * A document with every text element's placeholders resolved against a screen's
 * own permitted values.
 *
 * Done once, on the document, before it reaches the renderer — rather than inside
 * ElementBody in render.tsx — so the editor's own preview (which shares that
 * renderer) keeps showing a designer exactly what they typed, "{{" included, while
 * a live kiosk or hall board never shows the customer a brace pair. `values` is
 * server-filtered already (screenValues in internal/api/screen.go): a page passing
 * an unfiltered map here would defeat the one point of filtering on the server.
 */
export function resolveDocument(doc: LayoutDocument, values: Record<string, string>): LayoutDocument {
  return {
    ...doc,
    elements: doc.elements.map((el) => {
      if (el.kind !== 'text' || !el.content) return el
      const content: LayoutText = {}
      for (const [lang, text] of Object.entries(el.content)) {
        content[lang] = resolvePlaceholders(text, values)
      }
      return { ...el, content }
    }),
  }
}

/** Whether an element is on a main display's Called screen ('call'; a counter display's is 'called'). */
export const onCall = (el: LayoutElement): boolean => el.screens?.includes('call') ?? false

/** Whether an element belongs on the kiosk, counter or main display screen being shown. */
export function onScreen(element: LayoutElement, screen: Screen | undefined, page?: string): boolean {
  // A display's Called screen draws its own elements only, and the board never draws them.
  if (screen === 'call' || onCall(element)) return screen === 'call' && onCall(element)
  if (!screen) return true
  if (element.screens && element.screens.length > 0 && !element.screens.includes(screen)) return false
  // A page narrows the choosing screen only. Asked without one — a gallery
  // thumbnail, a design drawn before pages existed — every page's elements are
  // drawn, which is what such a design always meant.
  if (screen === 'choosing' && page && element.pages && element.pages.length > 0) {
    return element.pages.includes(page)
  }
  return true
}

/** Lowest depth first, and by identifier within a depth so drawing is stable. */
export function drawOrder(elements: LayoutElement[]): LayoutElement[] {
  return [...elements].sort((a, b) => (a.z ?? 0) - (b.z ?? 0) || a.id.localeCompare(b.id))
}
